import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";

/* Pieces the four data panels share. */

/** A token id in the page's own digits — Persian on /fa. Ids can exceed a
 *  safe integer, so the digits are mapped as text, never parsed. */
export function tokenDigits(id: string | number, isFa: boolean) {
  const text = String(id);
  return isFa ? text.replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]) : text;
}

export const shortAddr = (addr: string | undefined) => (addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "—");

export function explorerTxBase(chain: string | undefined) {
  const c = chain?.toLowerCase();
  if (c === "matic" || c === "polygon") return "https://polygonscan.com/tx/";
  if (c === "bsc" || c === "binance") return "https://bscscan.com/tx/";
  return "https://etherscan.io/tx/";
}

/** A transaction hash worth linking: 0x and 64 hex digits — a record still on its way (NFTBaz's own offer) has `null`. */
export const isTxHash = (hash: unknown): hash is string => typeof hash === "string" && /^0x[0-9a-f]{64}$/i.test(hash);

/** The explorer of the transaction's own chain; nothing to open (an unrecorded hash) keeps its place, empty. */
export function TxLink({ chain, hash, label }: { chain: string | undefined; hash: string | null | undefined; label: string }) {
  if (!isTxHash(hash)) return <span className="v2-cd-tx" data-none aria-hidden />;
  return (
    <a className="v2-cd-tx v2-focus" href={`${explorerTxBase(chain)}${hash}`} target="_blank" rel="noopener noreferrer" aria-label={`${label} — ${shortAddr(hash)}`} title={label}>
      <ExternalLink aria-hidden />
    </a>
  );
}

export function RetryButton({ label, onRetry }: { label: string; onRetry: () => void }) {
  return <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onRetry}>{label}</button>;
}

/** Rows drawn in shimmer while a table's first page is on its way. */
export function RowsSkeleton({ label, rows = 6 }: { label: string; rows?: number }) {
  return (
    <div className="v2-cd-table v2-cd-skel" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="v2-cd-skel-row">
          <span className="v2-cd-shimmer v2-cd-skel-a" />
          <span className="v2-cd-shimmer v2-cd-skel-b" />
          <span className="v2-cd-shimmer v2-cd-skel-c" />
        </span>
      ))}
    </div>
  );
}

/** A statement that is neither an error nor empty — the list stopped for a
 *  reason the reader is owed (an endpoint that repeats itself, a safety cap). */
export function Note({ children }: { children: ReactNode }) {
  return <p className="v2-cd-note">{children}</p>;
}

export function dateTime(iso: string, locale: string) {
  return new Date(iso).toLocaleDateString(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}


export { tokenAmount } from "@/lib/figure";
